import { type RefObject, useLayoutEffect, useRef } from 'react';
import { isEmpty } from 'lodash';

import { flashLogEntry, logEntryElementId, prefersReducedMotion } from '../../utils';

/** How long a message takes to move from its queue row into its entry. */
const MOVE_MS = 350;

/** A queue row that has just gone, and the entry the log now draws for its message. */
interface Arrival {
  messageId: string;
  row: HTMLElement;
  /** Where the row was, as last measured. */
  from: DOMRect;
  entry: HTMLElement;
}

/** Whether any of a box shows in the scroller's visible area. */
const isInView = (box: DOMRect, scroller: HTMLElement): boolean => {
  const view = scroller.getBoundingClientRect();

  return box.bottom > view.top && box.top < view.bottom;
};

/** Scrolls the scroller the least that shows the whole of a box, and leaves the page where it is. */
const scrollToShow = (box: DOMRect, scroller: HTMLElement): void => {
  const view = scroller.getBoundingClientRect();
  if (box.top < view.top) scroller.scrollTop -= view.top - box.top;
  else if (box.bottom > view.bottom) scroller.scrollTop += box.bottom - view.bottom;
};

/**
 * A copy of the queue row, drawn over the page where the row was and moved into the entry's box as it fades out: the
 * row itself went with the commit that drew the entry. The copy is the page's for the move only.
 */
const moveIntoEntry = (row: HTMLElement, from: DOMRect, to: DOMRect): void => {
  const ghost = row.cloneNode(true);
  if (!(ghost instanceof HTMLElement) || typeof ghost.animate !== 'function' || !from.width || !from.height) return;

  ghost.setAttribute('aria-hidden', 'true');
  Object.assign(ghost.style, {
    position: 'fixed',
    left: `${from.left}px`,
    top: `${from.top}px`,
    width: `${from.width}px`,
    height: `${from.height}px`,
    margin: '0',
    pointerEvents: 'none',
    transformOrigin: 'top left',
    zIndex: '1',
  });
  document.body.appendChild(ghost);

  const animation = ghost.animate(
    [
      { transform: 'none', opacity: 1 },
      {
        transform: `translate(${to.left - from.left}px, ${to.top - from.top}px) scale(${to.width / from.width}, ${to.height / from.height})`,
        opacity: 0,
      },
    ],
    { duration: MOVE_MS, easing: 'ease-in-out' }
  );
  const remove = () => ghost.remove();
  animation.onfinish = remove;
  animation.oncancel = remove;
};

/**
 * Sees a message the log takes in from the queue into its entry: the commit that draws the handler's message drops
 * its queue row, and a copy of the row moves from where the row was into the entry, fading as it goes, while the
 * entry flashes as an entry the reader is taken to does. A reader at the newest end of the log is kept there; one
 * who scrolled away is left where they are, unless the message is one they sent from this composer, which the log
 * scrolls just far enough to show; and a move into an entry out of their view is not drawn. For a reader who asked
 * for less motion, the message is swapped in with nothing moving or flashing.
 *
 * Every row still in the queue is measured after each commit, for the commit that takes its message in, when the
 * row has already gone. Returns the ref the queue strip gives each row, by its message's id.
 */
export const useDissolveQueueItem = (
  scrollerRef: RefObject<HTMLElement>,
  /** The entry of each handler's message the log holds, by the message's id. */
  turnEventIds: ReadonlyMap<string, string>,
  isReversed: boolean,
  /** The ids of the messages the handler sent from this composer. */
  sentHere: ReadonlySet<string>,
  /** Whether the log keeps the reader at its newest end as it draws more. */
  shouldFollow: () => boolean
): ((messageId: string) => (element: HTMLElement | null) => void) => {
  const rowsRef = useRef(new Map<string, HTMLElement>());
  const boxesRef = useRef(new Map<string, DOMRect>());

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const rows = rowsRef.current;
    const arrivals: Arrival[] = [];
    rows.forEach((row, messageId) => {
      if (row.isConnected) return;

      rows.delete(messageId);
      const from = boxesRef.current.get(messageId);
      const eventId = turnEventIds.get(messageId);
      const entry = eventId ? document.getElementById(logEntryElementId(eventId)) : null;
      if (from && entry) arrivals.push({ messageId, row, from, entry });
    });

    if (scroller && !isEmpty(arrivals)) {
      const sentHereArrival = arrivals.find(({ messageId }) => sentHere.has(messageId));
      if (shouldFollow()) scroller.scrollTop = isReversed ? 0 : scroller.scrollHeight;
      else if (sentHereArrival) scrollToShow(sentHereArrival.entry.getBoundingClientRect(), scroller);

      if (!prefersReducedMotion()) {
        arrivals.forEach(({ row, from, entry }) => {
          const to = entry.getBoundingClientRect();
          if (isInView(to, scroller)) moveIntoEntry(row, from, to);
          flashLogEntry(entry);
        });
      }
    }

    boxesRef.current = new Map(Array.from(rows, ([messageId, row]) => [messageId, row.getBoundingClientRect()]));
  });

  return (messageId) => (element) => {
    if (element) rowsRef.current.set(messageId, element);
  };
};
